/**
 * RECRUITER LEVEL PROGRESS BAR
 *
 * Displays HR user's current level, XP progress, and progress to next level
 * with animated progress bar and professional business theme
 */

import React, { useEffect, useState } from 'react';
import { TrendingUp, Briefcase, Award } from 'lucide-react';
import { HR_LEVELS } from '../../constants/hrGamification';

interface RecruiterLevelProgressBarProps {
  currentXP: number;
  currentLevel: number;
  levelName: string;
  animated?: boolean;
  showDetails?: boolean;
  className?: string;
}

const RecruiterLevelProgressBar: React.FC<RecruiterLevelProgressBarProps> = ({
  currentXP,
  currentLevel,
  levelName,
  animated = true,
  showDetails = true,
  className = ''
}) => {
  // State for animated XP counter
  const [displayXP, setDisplayXP] = useState(0);

  // Find current and next level information
  const levelInfo = HR_LEVELS.find(l => l.level === currentLevel) || HR_LEVELS[0];
  const nextLevel = HR_LEVELS.find(l => l.level === currentLevel + 1);

  // Calculate progress percentage
  const xpInCurrentLevel = currentXP - levelInfo.minXP;
  const xpNeededForNextLevel = nextLevel ? nextLevel.minXP - levelInfo.minXP : 1;
  const progressPercentage = Math.min((xpInCurrentLevel / xpNeededForNextLevel) * 100, 100);

  // Animate XP counter on mount and when XP changes
  useEffect(() => {
    if (!animated) {
      setDisplayXP(currentXP);
      return;
    }

    const duration = 1500; // 1.5 seconds animation
    const steps = 60; // 60 frames
    const increment = (currentXP - displayXP) / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setDisplayXP(currentXP);
        clearInterval(timer);
      } else {
        setDisplayXP(prev => prev + increment);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [currentXP, animated, displayXP]);

  // Check if user is close to leveling up (80%+)
  const isNearNextLevel = progressPercentage >= 80;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      {/* Header Section */}
      <div className="flex items-center justify-between mb-4">
        {/* Current Level Display */}
        <div className="flex items-center space-x-3">
          <div className="text-4xl">{levelInfo.emoji}</div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Level {currentLevel} {levelName}
            </h3>
            <p className="text-sm text-gray-600">{levelInfo.description}</p>
          </div>
        </div>

        {/* Next Level Preview */}
        {nextLevel && (
          <div className="text-right">
            <div className="text-2xl opacity-30">{nextLevel.emoji}</div>
            <p className="text-xs text-gray-500 mt-1">Next Level</p>
          </div>
        )}
      </div>

      {/* XP Progress Section */}
      <div className="mb-4">
        {/* XP Display and Target */}
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center space-x-2">
            <Briefcase className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-700">
              {Math.floor(displayXP).toLocaleString()} XP
            </span>
          </div>
          {nextLevel && (
            <span className="text-sm text-gray-500">
              {xpNeededForNextLevel - xpInCurrentLevel} XP to Level {nextLevel.level}
            </span>
          )}
        </div>

        {/* Progress Bar Container */}
        <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden">
          {/* Progress Fill with Gradient */}
          <div
            className={`absolute top-0 left-0 h-full bg-gradient-to-r ${levelInfo.color} transition-all duration-1000 ease-out ${
              isNearNextLevel ? 'animate-pulse-glow' : ''
            }`}
            style={{ width: `${progressPercentage}%` }}
          >
            {/* Shine Effect - Moving light across bar */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shine" />
          </div>

          {/* Percentage Text Overlay */}
          <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-gray-700">
            {progressPercentage.toFixed(0)}%
          </div>
        </div>
      </div>

      {/* Detailed Stats Cards */}
      {showDetails && (
        <div className="grid grid-cols-2 gap-3">
          {/* Current Level Card */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-semibold text-blue-900">Current Level</span>
            </div>
            <div className="text-xl font-bold text-blue-900">
              {currentLevel}
            </div>
          </div>

          {/* Total XP Card */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-1">
              <Award className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-semibold text-purple-900">Total XP</span>
            </div>
            <div className="text-xl font-bold text-purple-900">
              {Math.floor(displayXP).toLocaleString()}
            </div>
          </div>
        </div>
      )}

      {/* Near Level-Up Encouragement Message */}
      {isNearNextLevel && nextLevel && (
        <div className="mt-4 bg-gradient-to-r from-yellow-50 to-orange-100 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm font-semibold text-yellow-900 text-center">
            🔥 Almost there! Just {xpNeededForNextLevel - xpInCurrentLevel} XP to reach{' '}
            {nextLevel.name} Level {nextLevel.level}!
          </p>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        /* Shine effect animation - creates moving light across progress bar */
        @keyframes shine {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .animate-shine {
          animation: shine 2s infinite;
        }

        /* Pulse glow animation - glowing effect when near level up */
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
          }
          50% {
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
          }
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default RecruiterLevelProgressBar;

